import { useState } from 'react';
import { X, UserPlus, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';
import Button from './ui/Button';
import Select from './ui/Select';
import { useAuthStore } from '../stores/authStore';

interface InviteMemberModalProps {
  organizationId: string;
  onClose: () => void;
  onSuccess: () => void;
}

// Permission matrix for member creation
const CREATION_PERMISSIONS: Record<string, { value: string; label: string; description: string }[]> = {
  OWNER: [
    { value: 'ADMIN', label: 'Admin', description: 'All permissions except ownership transfer' },
    { value: 'MANAGER', label: 'Manager', description: 'Manage jobs and assign staff' },
    { value: 'STAFF', label: 'Staff', description: 'Work on assigned jobs' },
    { value: 'VIEWER', label: 'Viewer', description: 'Read-only access' },
  ],
  ADMIN: [
    { value: 'MANAGER', label: 'Manager', description: 'Manage jobs and assign staff' },
    { value: 'STAFF', label: 'Staff', description: 'Work on assigned jobs' },
    { value: 'VIEWER', label: 'Viewer', description: 'Read-only access' },
  ],
  MANAGER: [
    { value: 'STAFF', label: 'Staff', description: 'Work on assigned jobs' },
    { value: 'VIEWER', label: 'Viewer', description: 'Read-only access' },
  ],
  STAFF: [],
  VIEWER: [],
};

export default function InviteMemberModal({ organizationId, onClose, onSuccess }: InviteMemberModalProps) {
  const { user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleInOrg, setRoleInOrg] = useState('STAFF');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ message: string; password?: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Get available roles based on current user's role
  const availableRoles = CREATION_PERMISSIONS[user?.roleInOrg || 'VIEWER'] || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(null);

    // Validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (!roleInOrg) {
      setError('Please select a role');
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await api.post(`/organizations/${organizationId}/invite-member`, {
        email,
        fullName: fullName || undefined,
        roleInOrg,
        organizationId,
      });

      // Show success message
      setSuccess({
        message: response.data.message,
        password: response.data.temporaryPassword, // Only if returned by backend
      });

      // Reset form
      setEmail('');
      setFullName('');
      setRoleInOrg('STAFF');

      // Call success callback after short delay
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Invite member error:', err);
      setError(err.response?.data?.error || 'Failed to invite member');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (availableRoles.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="glass-card max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-glass-text">Invite Member</h2>
            <button onClick={onClose} className="text-glass-text-3 hover:text-glass-text">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-amber-500 dark:text-amber-300 mx-auto mb-3" />
            <p className="text-glass-text-2">
              Your role ({user?.roleInOrg}) does not have permission to invite new members.
            </p>
            <Button onClick={onClose} className="mt-4">
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-glass">
          <div>
            <h2 className="text-2xl font-semibold tracking-tightest text-glass-text">Invite Team Member</h2>
            <p className="text-sm text-glass-text-2 mt-1">Add a new member to your organization</p>
          </div>
          <button
            onClick={onClose}
            className="text-glass-text-3 hover:text-glass-text transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto glass-scroll p-6">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-emerald-500 dark:text-emerald-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-glass-text mb-2">
                Member Invited Successfully!
              </h3>
              <p className="text-glass-text-2 mb-4">{success.message}</p>
              
              {success.password && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 mb-4">
                  <p className="text-sm font-medium text-amber-500 dark:text-amber-300 mb-2">
                    Temporary Password Generated:
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="bg-glass-2 px-3 py-2 rounded border border-amber-400/30 text-amber-500 dark:text-amber-300 font-mono">
                      {showPassword ? success.password : '••••••••'}
                    </code>
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-2 hover:bg-amber-400/15 rounded text-amber-500 dark:text-amber-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-glass-text-3 mt-2">
                    Please share this password securely with the new member
                  </p>
                </div>
              )}

              <Button onClick={onClose}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-500 dark:text-rose-300 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-rose-500 dark:text-rose-300">{error}</p>
                </div>
              )}

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-glass-text-2 mb-1">
                  Email Address <span className="text-rose-500 dark:text-rose-300">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  placeholder="member@example.com"
                  required
                  disabled={isSubmitting}
                />
                <p className="text-xs text-glass-text-3 mt-1">
                  If the email doesn't exist, a new account will be created
                </p>
              </div>

              {/* Full Name Input */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-glass-text-2 mb-1">
                  Full Name (Optional)
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
              </div>

              {/* Role Selection */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-glass-text-2 mb-1">
                  Role <span className="text-rose-500 dark:text-rose-300">*</span>
                </label>
                <Select
                  id="role"
                  value={roleInOrg}
                  onChange={(e) => setRoleInOrg(e.target.value)}
                  className="w-full"
                  required
                  disabled={isSubmitting}
                >
                  {availableRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Select>
                {availableRoles.find((r) => r.value === roleInOrg) && (
                  <p className="text-xs text-glass-text-3 mt-1">
                    {availableRoles.find((r) => r.value === roleInOrg)?.description}
                  </p>
                )}
              </div>

              {/* Permission Info */}
              <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4">
                <h4 className="font-medium text-accent2 text-sm mb-2">
                  Your Permission Level ({user?.roleInOrg})
                </h4>
                <p className="text-xs text-glass-text-2">
                  You can invite: {availableRoles.map((r) => r.label).join(', ')}
                </p>
              </div>

              {/* Available Roles Legend */}
              <div className="rounded-xl border border-glass bg-glass-2 p-4">
                <h4 className="font-medium text-glass-text text-sm mb-2">Role Descriptions</h4>
                <div className="space-y-2">
                  {availableRoles.map((role) => (
                    <div key={role.value} className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-accent2 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-medium text-glass-text">{role.label}</p>
                        <p className="text-xs text-glass-text-3">{role.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex justify-end gap-3 p-6 border-t border-glass bg-glass-2">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Inviting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Member
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

